import { computed, ref, shallowRef } from 'vue'

import type {
  HapticImpactStyle,
  HapticNotificationType,
  TelegramContact,
  TelegramContactResponse,
  TelegramWebApp,
  TelegramWebAppUser,
} from '@/types/telegram'

/*
 * Именно shallowRef, не ref: обычный ref оборачивает WebApp в глубокий Proxy,
 * а Telegram объявляет HapticFeedback, BackButton и LocationManager через
 * `Object.defineProperty` без writable/configurable. Proxy обязан вернуть то же
 * значение, что лежит в объекте, — Vue возвращает реактивную обёртку, и любое
 * обращение к такому полю падает с TypeError. Реактивность здесь нужна только
 * на «появился/не появился», а её shallowRef даёт.
 */
const webApp = shallowRef<TelegramWebApp | null>(null)
const isReady = ref(false)

/** HapticFeedback появился в Bot API 6.1 — в клиентах постарше вызов только сыплет предупреждения */
const HAPTIC_VERSION = '6.1'

/** Окно «поделиться номером» появилось в Bot API 6.9 */
const CONTACT_VERSION = '6.9'

/** Кнопка «назад» в шапке Telegram — с Bot API 6.1 */
const BACK_BUTTON_VERSION = '6.1'

function hapticApi(): TelegramWebApp['HapticFeedback'] {
  const tg = webApp.value
  if (!tg?.HapticFeedback || !tg.isVersionAtLeast(HAPTIC_VERSION)) return undefined
  return tg.HapticFeedback
}

function parseContact(value: TelegramContact | string | undefined): TelegramContact | null {
  if (!value) return null
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value) as TelegramContact
  } catch {
    return null
  }
}

/**
 * Телефон из ответа `requestContact`. Форма ответа зависит от версии клиента:
 * сначала разворачиваем подписанную query-строку, затем сам контакт — он
 * приходит и объектом, и JSON-строкой.
 */
function readContactPhone(data?: TelegramContactResponse): string {
  if (!data) return ''

  const source = data.response
    ? (Object.fromEntries(new URLSearchParams(data.response)) as TelegramContactResponse)
    : data

  return parseContact(source.contact)?.phone_number ?? ''
}

function backButtonApi(): TelegramWebApp['BackButton'] | undefined {
  const tg = webApp.value
  if (!tg?.BackButton || !tg.isVersionAtLeast(BACK_BUTTON_VERSION)) return undefined
  return tg.BackButton
}

/*
 * Чего от кнопки хотят экраны. Запоминаем у себя, потому что просят её раньше,
 * чем поднимается сам Telegram: композаблы работают в setup, а `init()` —
 * в `onMounted`. Без этой памяти подписка уходила в ещё не существующий
 * `WebApp`, кнопка показывалась, а нажатие не доходило никуда.
 */
let backHandler: (() => void) | null = null
let isBackVisible = false

/** Переносим отложенное состояние на настоящую кнопку — зовём из `init()`. */
function applyBackButton(): void {
  const api = backButtonApi()
  if (!api) return

  if (backHandler) api.onClick(backHandler)
  if (isBackVisible) api.show()
  else api.hide()
}

/**
 * Кнопка «назад» в шапке Telegram. Пока она показана, клиент Telegram шлёт
 * в неё и системную кнопку «назад» на Android, и жест возврата — то есть
 * мини-апп перестаёт закрываться там, где клиент хотел вернуться на шаг.
 *
 * Вне Telegram и в старых клиентах — тихий no-op, как и `haptic`.
 */
export const backButton = {
  show(): void {
    isBackVisible = true
    backButtonApi()?.show()
  },
  hide(): void {
    isBackVisible = false
    backButtonApi()?.hide()
  },
  onClick(handler: () => void): void {
    backHandler = handler
    backButtonApi()?.onClick(handler)
  },
  offClick(handler: () => void): void {
    if (backHandler === handler) backHandler = null
    backButtonApi()?.offClick(handler)
  },
}

/**
 * Тактильный отклик Telegram. Вне Telegram, в вебе и в старых клиентах — тихий
 * no-op, поэтому вызывать можно откуда угодно без проверок на окружение.
 *
 * Объект живёт в модуле, а не в `useTelegram()`: ссылка стабильна между
 * вызовами, и его можно передавать хоть в обработчик, хоть в другой композабл.
 */
export const haptic = {
  /** Действие сработало: «+» на карточке, добавление в корзину, переключение */
  impact(style: HapticImpactStyle = 'light'): void {
    hapticApi()?.impactOccurred(style)
  },
  /** Итог операции: заказ ушёл, запрос упал, код не подошёл */
  notify(type: HapticNotificationType): void {
    hapticApi()?.notificationOccurred(type)
  },
  /** Выбор поменялся: таб, радиокнопка, элемент списка */
  select(): void {
    hapticApi()?.selectionChanged()
  },
}

/**
 * Обёртка над Telegram.WebApp.
 * Вне Telegram (локальная разработка в браузере) методы становятся no-op,
 * а вёрстка получает 100vh — так макет можно смотреть без бота.
 */
export function useTelegram() {
  const user = computed<TelegramWebAppUser | null>(() => webApp.value?.initDataUnsafe.user ?? null)
  const isTelegram = computed(() => webApp.value !== null)

  /**
   * Параметр запуска: `t.me/<bot>/<app>?startapp=<value>`. По нему бот уводит
   * клиента не на главную, а туда, о чём написал, — например в переписку
   * с поддержкой из пуша «вам ответили».
   */
  const startParam = computed(() => webApp.value?.initDataUnsafe.start_param ?? '')

  function init(): void {
    const tg = window.Telegram?.WebApp
    /*
     * Вне Telegram скрипт telegram-web-app.js всё равно создаёт WebApp
     * (эмуляция v6.0 с пустым initData) — вызовы методов там только сыплют
     * предупреждения в консоль. Реальный клиент отличаем по initData.
     */
    if (!tg || tg.initData === '') {
      document.documentElement.style.setProperty('--tg-viewport-height', '100dvh')
      isReady.value = true
      return
    }

    webApp.value = tg
    tg.ready()
    tg.expand()
    tg.disableVerticalSwipes?.()
    tg.setBackgroundColor?.('#F7F7F7')
    tg.setHeaderColor?.('#F7F7F7')

    syncViewport()
    tg.onEvent('viewportChanged', syncViewport)

    /* Экраны просили кнопку «назад» ещё до init — переносим их просьбы сюда */
    applyBackButton()

    isReady.value = true
  }

  function syncViewport(): void {
    const height = webApp.value?.viewportStableHeight
    if (height) {
      document.documentElement.style.setProperty('--tg-viewport-height', `${height}px`)
    }
  }

  /**
   * Номер клиента. В `initData` его нет и быть не может: Telegram отдаёт
   * телефон только после явного согласия в нативном окне (Bot API 6.9).
   *
   * Отказ, старый клиент и запуск вне Telegram — пустая строка, поле остаётся
   * на ручной ввод.
   */
  function requestContact(): Promise<string> {
    const tg = webApp.value
    if (!tg?.requestContact || !tg.isVersionAtLeast(CONTACT_VERSION)) return Promise.resolve('')

    return new Promise((resolve) => {
      tg.requestContact?.((granted, data) => {
        resolve(granted ? readContactPhone(data) : '')
      })
    })
  }

  function close(): void {
    webApp.value?.close()
  }

  /**
   * Открыть внешний адрес. В Telegram зовём `openLink` — он уводит ссылку
   * в системный браузер; во встроенном webview платёжные страницы часто
   * не работают. Вне Telegram остаётся обычная вкладка.
   */
  function openLink(url: string): void {
    if (!url) return

    const tg = webApp.value
    if (tg?.openLink) {
      tg.openLink(url)
      return
    }

    window.open(url, '_blank', 'noopener')
  }

  return { isReady, isTelegram, user, startParam, init, haptic, close, openLink, requestContact }
}
